import { gatedFetch } from '../utils/requestGate';

const PROD_API = 'https://api.ohbellezaoficial.com';
const RAW_API = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
const API_BASE = RAW_API || (process.env.NODE_ENV === 'production' ? PROD_API : '');
/** En local, sin REACT_APP_API_URL el proxy de CRA a veces no sirve /imagenes → API directo. */
const IMAGE_BASE =
  API_BASE ||
  (process.env.NODE_ENV !== 'production' ? 'http://localhost:8081' : PROD_API);

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isProdApi() {
  return (
    process.env.NODE_ENV === 'production' ||
    /ohbellezaoficial\.com/i.test(String(API_BASE))
  );
}

function networkError(kind) {
  if (kind === 'timeout') {
    return new Error(
      isProdApi()
        ? 'El servidor tardó demasiado. Espera unos segundos y pulsa Reintentar.'
        : 'Timeout. ¿Está el backend en marcha?'
    );
  }
  return new Error(
    isProdApi()
      ? 'No se pudo conectar con el servidor. Espera unos segundos y pulsa Reintentar.'
      : 'No se pudo conectar. Inicia el backend: cd backend && npm start'
  );
}

/**
 * @param {string} path
 * @param {RequestInit & { retries?: number, timeoutMs?: number }} [options]
 */
async function request(path, options = {}) {
  const { retries = 0, timeoutMs = 22000, signal, headers, ...rest } = options;
  let lastErr;

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (signal?.aborted) {
      const err = new Error('Aborted');
      err.name = 'AbortError';
      throw err;
    }

    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const onOuterAbort = () => {
      try {
        ctrl?.abort();
      } catch {
        /* ignore */
      }
    };
    signal?.addEventListener?.('abort', onOuterAbort);

    let timer = null;
    if (ctrl && timeoutMs > 0) {
      timer = setTimeout(() => {
        try {
          ctrl.abort();
        } catch {
          /* ignore */
        }
      }, timeoutMs);
    }

    try {
      const res = await gatedFetch(`${API_BASE}/api${path}`, {
        ...rest,
        headers: { 'Content-Type': 'application/json', ...(headers || {}) },
        signal: ctrl?.signal || signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Error en la solicitud');
      return json;
    } catch (err) {
      if (err?.name === 'AbortError') {
        if (signal?.aborted) throw err;
        // Timeout propio (no abort externo)
        lastErr = networkError('timeout');
      } else if (err?.message && !/Failed to fetch|NetworkError|Load failed/i.test(err.message)) {
        // Error de negocio (JSON) — no reintentar
        throw err;
      } else {
        lastErr = networkError('network');
      }
      if (attempt < retries) {
        await sleep(500 * (attempt + 1));
        continue;
      }
    } finally {
      if (timer) clearTimeout(timer);
      signal?.removeEventListener?.('abort', onOuterAbort);
    }
  }

  throw lastErr || networkError('network');
}

export function imageUrl(imagen) {
  if (!imagen) return null;
  if (imagen.startsWith('http')) return imagen;
  const base = IMAGE_BASE || '';
  return `${base}/${imagen.replace(/^\//, '')}`;
}

export function formatCOP(value) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

export function displayNombre(product) {
  const n = product?.nombre || '';
  if (!n || n.startsWith('Producto ')) return '';
  return n;
}

export const api = {
  ping: () => request('/ping'),
  getCategorias: () => request('/categorias', { retries: 1, timeoutMs: 15000 }),
  getProductos: ({ q = '', subcategoria = '', offset = 0, limit = 48, signal } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (subcategoria && subcategoria !== 'Todas') params.set('subcategoria', subcategoria);
    params.set('canal', 'web');
    params.set('offset', String(offset));
    params.set('limit', String(limit));
    // 2 reintentos: cuando el hosting hace CPU fault, el primer fetch suele morir.
    return request(`/productos?${params}`, { signal, retries: 2, timeoutMs: 22000 });
  },
  getProducto: (codigo) => request(`/productos/${encodeURIComponent(codigo)}?canal=web`, { retries: 1 }),
  crearPedido: (body) =>
    request('/pedidos', { method: 'POST', body: JSON.stringify(body) }),
  syncCartReserva: (body) =>
    request('/carrito/reserva', { method: 'POST', body: JSON.stringify(body) }),
  clearCartReserva: (sessionId) =>
    request(`/carrito/reserva?session_id=${encodeURIComponent(sessionId || '')}`, {
      method: 'DELETE',
    }),
};
